/**
 * 22 - Wiretap (10 §2): a small signals post - the field telephone exchange in the lee of its hut, a guard
 * house, two sentries who walk round the exchange. Ten seconds held still fits the tap; being SEEN within
 * 25 m of the exchange, before or after, gives the tap away. The intended answers are mission 9's: the
 * uniform from the guard house, or the gap between the sentries' sweeps.
 */

import type { Point } from '../types';
import {
  checker, clutter, countKind, guardAtDoor, guardsAbout, insideRect, required, ruleOf, structure, targetsOf, uniformDonor,
} from './helpers';
import { RECALL, sweepRound } from './helpers3b';
import type { ArchetypeSpec } from './types';

const HOLD_SECONDS = 10;
const UNSEEN_RADIUS = 25;

/** Sentries on the round: two, and a third when the garrison is a strong one. */
const sweepersFor = (threat: number): number => (threat >= 7 ? 3 : 2);

export const wiretap: ArchetypeSpec = {
  id: 'wiretap',
  name: 'WIRETAP',
  nativeBiome: 'arctic',
  baseThreat: 3,
  lengthTiles: 160,
  clockScale: 1,
  startAlert: 0,
  garrison: 'regular',
  perimeter: 'wire',
  sealed: false,
  layout: 'compound',
  // A small post: signals, not a garrison town.
  size: { w: [46, 52], d: [28, 32] },
  river: false,
  patrolLoop: 'inside',
  breach: { min: 0, max: 0 },

  build(g) {
    const placed: Point[] = [];
    const hut = required(g, 'hut', { from: placed, minDist: 0, margin: 6.5 });
    // The exchange stands in the open beside its hut, where the lines come in: room all round it for the sentries' beat.
    const side = g.rng.chance(0.5) ? 1 : -1;
    const exchange = g.map.place('telephoneExchange', hut.x + side * 6, hut.z + 0.5, { margin: 0.8, target: true });
    if (!exchange) g.map.problems.push('wiretap: no room for the exchange beside the signals hut');
    const house = required(g, 'guardHouse', { minDist: 14, from: placed, margin: 1.4 });
    structure(g, g.kit.shelter, { minDist: 10, from: placed, margin: 1.4 });
    structure(g, 'alarmPost', { margin: 1 });
    clutter(g, 'crate', 3);

    // The man whose uniform solves the mission stands at the guard house door.
    guardAtDoor(g, house);
    const kind = g.elite ? 'elite' : 'rifleman';
    if (!sweepRound(g, hut, sweepersFor(g.threat), kind, 8)) {
      g.map.problems.push('wiretap: no room for the sentries to walk round the exchange');
    }
    guardsAbout(g, g.rng.int(2));
    const id = exchange?.id ?? -1;
    return {
      objective: {
        kind: 'useObjects', label: 'FIT THE WIRETAP', targetPropIds: [id], minFraction: 1,
        holdSeconds: HOLD_SECONDS, useLabel: 'FIT THE TAP',
        rules: [
          {
            type: 'notSeenNear', propId: id, radius: UNSEEN_RADIUS,
            orders: `DO NOT BE SEEN WITHIN ${UNSEEN_RADIUS} M OF THE EXCHANGE, BEFORE OR AFTER THE TAP IS FITTED`,
            reason: 'SEEN AT THE EXCHANGE - THEY WILL FIND THE TAP',
          },
          RECALL,
        ],
      },
    };
  },

  asserts(def, probe) {
    const { check, out } = checker('wiretap');
    const [exchange] = targetsOf(def, 'telephoneExchange');
    check(exchange !== undefined && def.objective.targetPropIds.length === 1, 'the exchange must be the one target');
    check(countKind(def, 'telephoneExchange') === 1, 'there must be exactly one exchange: no lookalikes');
    check(exchange?.target === true, 'the exchange must be flagged for the strip map');
    if (exchange) {
      check(insideRect(def.goalZone, exchange.x, exchange.z), 'the exchange must stand inside the signals post');
      check(probe.breach(exchange.id) === 0, 'the exchange must be reachable through the open gates');
    }
    check(
      def.objective.kind === 'useObjects' && def.objective.holdSeconds === HOLD_SECONDS,
      `objective must be useObjects with a ${HOLD_SECONDS} s hold`,
    );
    const seen = ruleOf(def, 'notSeenNear');
    check(
      seen !== undefined && seen.propId === exchange?.id && seen.radius === UNSEEN_RADIUS,
      `being seen within ${UNSEEN_RADIUS} m of the exchange must matter`,
    );
    check(countKind(def, 'guardHouse') >= 1, 'needs a guard house: the uniform is the intended way in');
    check(uniformDonor(def, 'guardHouse'), 'a guard must stand at the guard house: his is the uniform to take');
    const sweeping = def.spawns.filter((s) => s.patrol !== undefined && insideRect(def.goalZone, s.x, s.z));
    check(sweeping.length >= sweepersFor(def.threat), 'sentries must walk round the exchange');
    return out;
  },
};
